import { Label } from '@/components/ui';
import { cn } from '@/helpers/ui-utils';
import { cva, VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { useState } from 'react';
import HelperText from '../helperText';

const textareaVariants = cva(
  [
    'border',
    'border-solid',
    'border-input',
    'transition-all',
    'duration-300',
    'ease-in',
    'text-foreground/95',
    'placeholder:text-muted-foreground/90',
    'mb-0',
    'min-h-24',
    'flex',
    'w-full',
    'rounded-xs',
    'px-3',
    'py-2',
    'leading-[20px]',
    'ring-transparent',
    'ring-4',
    'ring-offset-0',
    'hover:border-input-hover',
    'focus:border-primary/50',
    'focus:shadow-none',
    'focus:ring-[rgba(225,225,254,0.7)]',
  ],
  {
    variants: {
      disabled: {
        true: 'opacity-50',
      },
      invalid: {
        true: [
          'border-warning',
          'hover:border-warning/20',
          'hover:ring-warning/20',
          'focus:border-warning',
          'focus:ring-4',
          'focus:ring-warning/20',
        ],
      },
    },
    defaultVariants: {},
  },
);

type TextareaCVAProps = {
  /**
   * Indicates if the textarea is in a disabled state. Prevents user interaction and applies visual disabled styling.
   * @default false
   */
  disabled?: VariantProps<typeof textareaVariants>['disabled'];

  /**
   * Indicates if the textarea is in an invalid/error state. Applies warning colors and styling.
   * @default false
   */
  invalid?: VariantProps<typeof textareaVariants>['invalid'];
};

export interface TextareaProps
  extends
    Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'disabled' | 'id'>,
    TextareaCVAProps {
  /**
   * The label text displayed above the textarea. Provides context about what content should be entered.
   */
  label: string;

  /**
   * Optional descriptive text displayed below the textarea. Used for additional guidance or validation feedback.
   */
  helperText?: string;

  /**
   * Required unique identifier for the textarea element. Used for label association and accessibility compliance.
   */
  id: string;
}

/**
 * Capture multi-line text input with built-in validation, character counting, and accessible form integration.
 *
 * @param props - Textarea properties. See {@link TextareaProps} for detailed prop documentation including inherited CVA variants.
 * @param ref - Forwarded ref to the textarea element
 * @returns Rendered textarea component
 *
 * @example
 * ```tsx
 * // Basic textarea
 * <Textarea id="message" label="Message" />
 *
 * // Textarea with helper text
 * <Textarea
 *   id="description"
 *   label="Description"
 *   helperText="Provide a detailed description"
 * />
 *
 * // Textarea with character limit
 * <Textarea
 *   id="comment"
 *   label="Comment"
 *   maxLength={500}
 *   helperText="Share your thoughts"
 * />
 *
 * // Invalid textarea with error message
 * <Textarea
 *   id="required"
 *   label="Required Field"
 *   invalid
 *   helperText="This field is required"
 * />
 *
 * // Disabled textarea
 * <Textarea
 *   id="readonly"
 *   label="Read Only"
 *   disabled
 *   value="Cannot be edited"
 * />
 * ```
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, helperText, id, disabled, ...props }, ref) => {
    const variantClass = textareaVariants({
      ...props,
      className,
    });
    const [text, setText] = useState('');

    const handleTextChange = (
      event: React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
      setText(event.target.value);
    };

    return (
      <div className={cn('flex w-full flex-col gap-1')}>
        <div className="flex justify-between">
          <Label htmlFor={id} className={cn({ 'opacity-50': disabled })}>
            {label}
          </Label>
          {props.maxLength && (
            <span
              aria-label="Character count"
              className="text-xs text-gray-500"
            >
              {text.length}/{props.maxLength}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <textarea
            onChange={handleTextChange}
            id={id}
            name={id}
            className={cn(variantClass)}
            ref={ref}
            disabled={disabled === true}
            {...props}
          />

          {helperText && (
            <HelperText id={id} invalid={props.invalid === true}>
              {helperText}
            </HelperText>
          )}
        </div>
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';

export default Textarea;
